import { useEffect, useRef, useState } from 'react';

export type BootStreamEvent =
  | {
      kind: 'boot_attempt';
      siteId: string | null;
      bootTokenId: string | null;
      stage: string;
      outcome: string;
      reason: string | null;
      clientIp: string | null;
      mac: string | null;
      createdAt: string;
    }
  | {
      kind: 'approval_change';
      siteId: string | null;
      bootTokenId: string;
      code: string;
      state: 'requested' | 'approved' | 'denied';
      actor: string | null;
      reason: string | null;
    };

export interface UseBootEventsOptions {
  /** Org slug — `:slug` path segment in the boot-service URL. */
  slug: string | null | undefined;
  /** Optional siteId filter. Server-side best-effort filtering; consumers
   *  should still apply their own bootTokenId filter for wizard scoping. */
  siteId?: string | null;
  /** Toggle the subscription. Set false to pause without unmounting. */
  enabled?: boolean;
  /** Fires for every parsed event. Keep this fn stable across renders
   *  (wrap in useCallback) to avoid re-subscribing on every render. */
  onEvent: (event: BootStreamEvent) => void;
}

export interface UseBootEventsState {
  connected: boolean;
  /** Last transport error message, or null. Stays set across reconnects
   *  so the caller can fall back to polling if it persists. */
  lastError: string | null;
}

// Reconnect backoff schedule (ms). Reset on successful `open`.
const RECONNECT_DELAYS = [2_000, 5_000, 15_000, 60_000];

/**
 * Subscribe to the boot-service `/b/{slug}/events` SSE stream via the
 * orchestrator BFF (`/api/boot/...`). Auth is via the same-origin cookie
 * the rest of the orchestrator uses — EventSource cannot send custom
 * Authorization headers, so cross-origin SSE would not work here.
 *
 * The hook:
 *   - Reconnects with backoff on error (2s → 5s → 15s → 60s, then
 *     repeats 60s). Resets the schedule on successful `open`.
 *   - Treats 4xx as terminal (auth or wrong URL — retrying won't help).
 *     Surfaces a non-null `lastError` so the caller can fall back to
 *     polling.
 *   - Pauses when the tab is backgrounded (`document.hidden`). EventSource
 *     keeps a connection open in the background but mobile browsers may
 *     drop it; pausing explicitly avoids surprise reconnect storms when
 *     the tab reactivates.
 *   - Closes the connection on unmount and clears any pending reconnect.
 */
export function useBootEvents({
  slug,
  siteId,
  enabled = true,
  onEvent,
}: UseBootEventsOptions): UseBootEventsState {
  const [connected, setConnected] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const onEventRef = useRef(onEvent);
  // Keep the latest onEvent in a ref so the connection effect doesn't
  // resubscribe on every parent re-render. The caller can still wrap
  // onEvent in useCallback if they want stable identity for other reasons.
  onEventRef.current = onEvent;

  // Pause on document.hidden — we still keep the hook "enabled" but skip
  // (re)connecting until the tab is visible again.
  const [visible, setVisible] = useState(
    typeof document === 'undefined' ? true : !document.hidden,
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!enabled || !slug || !visible) {
      setConnected(false);
      return;
    }

    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let unmounted = false;

    const url = (() => {
      const base = `/api/boot/b/${encodeURIComponent(slug)}/events`;
      return siteId ? `${base}?siteId=${encodeURIComponent(siteId)}` : base;
    })();

    const connect = () => {
      if (unmounted) return;
      source = new EventSource(url);

      source.addEventListener('open', () => {
        attempt = 0;
        setConnected(true);
        setLastError(null);
      });

      const handler = (e: MessageEvent) => {
        try {
          const parsed = JSON.parse(e.data as string) as BootStreamEvent;
          onEventRef.current(parsed);
        } catch {
          // Malformed payload — drop silently. The server controls the
          // wire format; a parse failure here would indicate a bug, not
          // an attacker, so don't surface to the user.
        }
      };
      source.addEventListener('boot_attempt', handler);
      source.addEventListener('approval_change', handler);

      source.addEventListener('error', () => {
        setConnected(false);
        // EventSource doesn't expose the HTTP status on error, but it
        // closes the connection when the response status is 4xx — we
        // can't distinguish 401/403 from a transient 5xx. Treat any
        // closed-state error as a transport failure and retry; if the
        // server is consistently rejecting us, the backoff will surface
        // a persistent `lastError` and the caller can fall back to
        // polling.
        if (source && source.readyState === EventSource.CLOSED) {
          source.close();
          source = null;
          const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)];
          attempt += 1;
          setLastError(`SSE disconnected, retrying in ${Math.round(delay / 1000)}s`);
          reconnectTimer = setTimeout(connect, delay);
        }
      });
    };

    connect();

    return () => {
      unmounted = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (source) source.close();
      setConnected(false);
    };
  }, [enabled, slug, siteId, visible]);

  return { connected, lastError };
}
